/**
 * Adversario do modo demonstracao.
 *
 * Nao e um motor de jogo forte, e nem tenta ser: existe para que a
 * demonstracao tenha alguem do outro lado sem precisar de servidor. Joga
 * dentro das regras de verdade — as mesmas do motor autoritativo — e escolhe
 * lances com uma heuristica simples e deterministica por semente, para que a
 * demonstracao seja reproduzivel.
 */
import type { GameRules, Move, Seat } from '@engine/games/types.ts';

/** Gerador deterministico: a mesma semente reproduz a mesma partida. */
export function createRandom(seed: number): () => number {
  let estado = seed >>> 0 || 1;
  return () => {
    estado ^= estado << 13;
    estado ^= estado >>> 17;
    estado ^= estado << 5;
    estado >>>= 0;
    return estado / 0x1_0000_0000;
  };
}

export interface BotOptions {
  readonly random: () => number;
  /** 0 = so aleatorio; 1 = sempre o melhor lance que a heuristica enxerga. */
  readonly skill?: number;
}

/**
 * Pontua um lance olhando um passo a frente:
 *   - lance que ganha na hora vale tudo;
 *   - lance que deixa o adversario ganhar na resposta e descartado;
 *   - empate vale menos que continuar jogando.
 * Um passo basta para o bot nao entregar partida de jogo da velha nem
 * pendurar dama de graca, que e o minimo para a demonstracao nao parecer
 * quebrada.
 */
function score<S>(rules: GameRules<S>, state: S, move: Move, seat: Seat): number {
  let proximo: S;
  try {
    proximo = rules.apply(state, move);
  } catch {
    return Number.NEGATIVE_INFINITY;
  }

  const resultado = rules.outcome(proximo);
  if (resultado) {
    if (resultado.kind === 'WIN') return resultado.seat === seat ? 1000 : -1000;
    return -5; // empate: melhor que perder, pior que seguir jogando
  }

  // O adversario responde: existe resposta que ganha na hora?
  for (const resposta of rules.legalMoves(proximo)) {
    try {
      const depois = rules.apply(proximo, resposta);
      const fim = rules.outcome(depois);
      if (fim?.kind === 'WIN' && fim.seat !== seat) return -500;
    } catch {
      // resposta invalida nao conta
    }
  }

  return 0;
}

export function chooseMove<S>(
  rules: GameRules<S>,
  state: S,
  options: BotOptions,
): Move | null {
  const moves = rules.legalMoves(state);
  if (moves.length === 0) return null;

  const seat = rules.turn(state);
  const skill = options.skill ?? 0.85;

  if (options.random() > skill) {
    return moves[Math.floor(options.random() * moves.length)] as Move;
  }

  let melhores: Move[] = [];
  let melhorNota = Number.NEGATIVE_INFINITY;

  for (const move of moves) {
    const nota = score(rules, state, move, seat);
    if (nota > melhorNota) {
      melhorNota = nota;
      melhores = [move];
    } else if (nota === melhorNota) {
      melhores.push(move);
    }
  }

  return melhores[Math.floor(options.random() * melhores.length)] as Move;
}

/** Tempo que o bot "pensa", para a partida nao parecer instantanea. */
export function thinkDelayMs(random: () => number): number {
  return 400 + Math.floor(random() * 900);
}
